import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/ui/modal/modal';
import { notify } from '@/ui/snackbar/notify';

import { modalState } from '../../state/modal.state';
import { settingsChangeUserRoleFormState } from '../settings/settings.state';
import { ChangeUserRole } from '../settings/user-management/change-user-role';
import { useActiveAndDisabledUsers } from '../settings/user-management/use-active-and-disabled-users';
import { editUserRole } from '@/api/v4/user.api';
import { useShareProject } from './share-modal/share-project/use-share-project';
import { useShareCompany } from './share-modal/share-company/use-share-company';
import { useAtom } from 'jotai';

export const ChangeUserRoleModal = () => {
  const { t } = useTranslation(['settings', 'default']);
  const [isDisabled, setIsDisabled] = useState(false);

  const [modal, setModal] = useAtom(modalState);
  const [changeUserRoleForm] = useAtom(settingsChangeUserRoleFormState);

  const { shareProject } = useShareProject();
  const { shareCompany } = useShareCompany();

  const { refreshUsers } = useActiveAndDisabledUsers();

  const closeModal = () => {
    setModal(null);
  };

  const handleOnChange = useCallback(async () => {
    if (!changeUserRoleForm) return;

    if (changeUserRoleForm.entities.length > 0) {
      const projectIds = changeUserRoleForm.entities
        .filter(entity => entity.type === 'project')
        .map(entity => entity.value);

      const companyIds = changeUserRoleForm.entities
        .filter(entity => entity.type === 'organization')
        .map(entity => entity.value);

      await Promise.all(
        projectIds.map(projectId =>
          shareProject({
            projectId,
            userIds: [changeUserRoleForm.userId],
            teamIds: [],
            newUsers: [],
          }),
        ),
      );

      await Promise.all(
        companyIds.map(organizationId =>
          shareCompany({
            organizationId,
            userIds: [changeUserRoleForm.userId],
            teamIds: [],
            newUsers: [],
          }),
        ),
      );
    }

    await editUserRole({
      userInAccountId: changeUserRoleForm.userId,
      roleId: changeUserRoleForm.newRoleId,
    });

    await refreshUsers();

    notify({ message: t`modal.changeRole.successPrompt` });
    closeModal();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeUserRoleForm]);

  if (!modal || modal.state !== 'changeUserRole') return null;

  const onCancel = () => {
    modal.onCancel();
    closeModal();
  };

  return (
    <Modal
      header={t`modal.changeRole.title`}
      onClose={onCancel}
      mainAction={{
        label: t`modal.changeRole.change`,
        onClick: handleOnChange,
      }}
      secondaryAction={{
        label: t`modal.changeRole.cancel`,
        onClick: onCancel,
      }}
      variant="info"
      disabled={isDisabled}
    >
      <ChangeUserRole
        userId={modal.userId}
        userDisplayName={modal.userDisplayName}
        currentRole={modal.currentRole}
        newRole={modal.newRole}
        hasLinkedEntities={modal.hasLinkedEntities}
        setActionDisable={setIsDisabled}
      />
    </Modal>
  );
};
